// CaseDetailPane — RIGHT pane: case header + tabs.
//
// PERMANENT tabs (always present, not closable): Chat, Overview, Documents.
// OPTIONAL tabs (user can open + close): Public Data (singleton) and one
// Comparison tab per compare job (named after the two documents). Each
// optional tab has an X to close it.

import { useEffect, useRef, useState } from 'react';
import { Loader2, X, GitCompare, Database, Edit2, Check } from 'lucide-react';
import {
  getCase,
  updateCase,
  reReviewCase,
  type Case,
  type CaseDetail as CaseDetailType,
} from '../api/cases';
import {
  listReviewRuns,
  getReviewRun,
  runFullReview,
  cancelReview,
  regenerateSummary,
  overrideFlag,
  unoverrideFlag,
  listAuditEntries,
  type ReviewRunDetail,
  type AuditEntry,
} from '../api/review';
import { downloadReviewRecordDocx, downloadFlagMemorandumDocx, downloadFlagMemorandumClaudeApiMd } from '../api/reviewRecord';
import { useToastStore } from '../stores/toast';
import StatusBadge from './StatusBadge';
import OverviewTab from './OverviewTab';
import DocumentsTab from './DocumentsTab';
import DiffTab from './DiffTab';
import ChatTab from './ChatTab';
import PublicDataTab from './PublicDataTab';
import SummaryTab from './SummaryTab';
import type { ChatMessage } from '../api/chat';

type PermanentTab = 'chat' | 'overview' | 'documents' | 'summary' | 'event-log';

// A dynamic comparison tab. `id` is the tab key (derived from job id);
// `jobId` is the backend compare job. One per compare run.
interface ComparisonTab {
  id: string;
  jobId: string;
  label: string;
  summary?: string;
  originalFilename?: string;
  revisionFilenames?: string[];
  originalDocId?: string;
  revisionDocId?: string;
}

interface CaseDetailPaneProps {
  caseSummary: Case; // lightweight from sidebar
  refreshKey?: number;
  onDocumentsChanged?: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

// Truncate a filename for tab label width. Keep the extension visible.
function shortName(name: string, max = 22): string {
  if (name.length <= max) return name;
  const dot = name.lastIndexOf('.');
  if (dot > 0 && name.length - dot <= 6) {
    const ext = name.slice(dot);
    const stem = name.slice(0, dot);
    return stem.slice(0, max - ext.length - 1) + '…' + ext;
  }
  return name.slice(0, max - 1) + '…';
}

// Build the tab label from the original + revision filenames.
function makeCompareLabel(
  originalFilename?: string,
  revisionFilenames?: string[],
): string | undefined {
  const revName =
    revisionFilenames && revisionFilenames.length > 0
      ? revisionFilenames.length === 1
        ? revisionFilenames[0]
        : `${revisionFilenames[0]} +${revisionFilenames.length - 1} more`
      : undefined;
  if (originalFilename && revName) {
    return `${shortName(originalFilename)} vs ${shortName(revName)}`;
  }
  if (originalFilename) return shortName(originalFilename);
  if (revName) return shortName(revName);
  return undefined;
}

export default function CaseDetailPane({ caseSummary, refreshKey, onDocumentsChanged, activeTab, onTabChange }: CaseDetailPaneProps) {
  const [caseData, setCaseData] = useState<Case>(caseSummary);
  const [detail, setDetail] = useState<CaseDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [whitewashJobId, setWhitewashJobId] = useState<string | null>(null);
  const [deficiencyJobId, setDeficiencyJobId] = useState<string | null>(null);
  // Dynamic comparison tabs (one per compare job). Ref keeps a current
  // snapshot so the onCompareJob handler can decide create-vs-update
  // without relying on stale closure state.
  const [comparisons, setComparisons] = useState<ComparisonTab[]>([]);
  const comparisonsRef = useRef<ComparisonTab[]>([]);
  const [compareSummary, setCompareSummary] = useState<string | null>(null);
  // Optional Public Data tab (singleton, closable). Opened on demand from
  // the Overview "Pull Public Data" / "View Public Data" buttons.
  const [showPublicData, setShowPublicData] = useState(false);
  // Bumped to ask PublicDataTab to run a refresh (0 = no-op).
  const [publicDataRefreshNonce, setPublicDataRefreshNonce] = useState(0);
  // Event Log tab (permanent). Audit entries loaded on demand when the
  // tab is selected.
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [externalMessage, setExternalMessage] = useState<ChatMessage | null>(null);
  const [externalSeq, setExternalSeq] = useState(0);
  // Inline case-title editing in the header.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  // Latest COMPLETED review run's detail (summary + score) for the Summary
  // tab. Loaded alongside `detail` on case switch / refresh.
  const [reviewRunDetail, setReviewRunDetail] = useState<ReviewRunDetail | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // Run-review state shared by the Overview + Chat tabs. Mirrors the legacy
  // CaseDetail.tsx state machine: reviewLoading (full-review kickoff),
  // rereviewLoading (re-pull + re-review kickoff), reviewInProgress (a run
  // is RUNNING), pollingRunId (the run being polled), cancellingReview
  // (cancel in flight), hasReview (any past run exists on this case),
  // reviewFailedMsg (last FAILED error).
  // The polling useEffect below drives status transitions + refreshes the
  // case + summary on COMPLETED/FAILED/CANCELLED.
  const [reviewLoading, setReviewLoading] = useState(false);
  const [rereviewLoading, setRereviewLoading] = useState(false);
  const [reviewInProgress, setReviewInProgress] = useState(false);
  const [pollingRunId, setPollingRunId] = useState<string | null>(null);
  const [cancellingReview, setCancellingReview] = useState(false);
  const [hasReview, setHasReview] = useState(false);
  const [reviewFailedMsg, setReviewFailedMsg] = useState<string | null>(null);
  // Which tab kicked off the current run ('overview' | 'chat'). Used to
  // decide whether to push a "Review completed" system message into the
  // chat stream on completion (only when the user fired it from chat).
  const [reviewStartedFrom, setReviewStartedFrom] = useState<'overview' | 'chat' | 'summary' | null>(null);
  const { addToast } = useToastStore();
  // Word (.docx) download in-flight flag for the Summary tab button.
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [downloadingFlagMemo, setDownloadingFlagMemo] = useState(false);
  const [downloadingFlagMemoClaudeApi, setDownloadingFlagMemoClaudeApi] = useState(false);
  // Summary regeneration in-flight flag for the Summary tab button.
  const [regeneratingSummary, setRegeneratingSummary] = useState(false);
  // Summary-tab-local error banner (regenerate failures). Kept separate
  // from the chat externalMessage so the error surfaces on the Summary
  // tab where the user clicked, not in the Chat tab.
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const syncComparisons = (next: ComparisonTab[]) => {
    comparisonsRef.current = next;
    setComparisons(next);
  };

  // Sync the local caseData to the incoming caseSummary IMMEDIATELY on case
  // switch so the right-pane header (case number / title / depositor /
  // issuer) reflects the new case instantly from the summary data the
  // sidebar already has. The getCase effect below then enriches it with
  // full detail once the fetch resolves. Without this, caseData held the
  // PREVIOUS case (useState only seeds on first mount) and the right pane
  // showed stale content until getCase resolved — visible lag when the
  // single backend worker was busy (e.g. post-drop intake AI pass).
  // Runs BEFORE the getCase effect (effects fire in definition order) so
  // the synchronous seed lands first.
  useEffect(() => {
    setCaseData(caseSummary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseSummary.id]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getCase(caseSummary.id)
      .then((d) => {
        setDetail(d);
        // Merge detail case into our local case state (keeps sidebar fields too).
        setCaseData({ ...caseSummary, ...d.case, type: caseSummary.type || d.case?.type });
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load case'))
      .finally(() => setLoading(false));
    // Tab state is owned by the parent (persists across case switches).
    // Comparisons + Public Data are per-case: clear them on case switch.
    // Keep the active PERMANENT tab (chat/overview/documents) so switching
    // cases does not yank the user off their tab; only fall back to Chat if
    // the current tab was an optional (comparison/public-data) tab that
    // won't exist in the new case, OR if the current tab is Summary but
    // the new case is a comparison case (Summary is hidden for those).
    syncComparisons([]);
    setWhitewashJobId(null);
    setDeficiencyJobId(null);
    setCompareSummary(null);
    setShowPublicData(false);
    setPublicDataRefreshNonce(0);
    setAuditEntries([]);
    setExternalMessage(null);
    const summaryHiddenForCase = (caseSummary.type || caseData?.type) === 'comparison';
    if (
      !['chat', 'overview', 'documents', 'summary', 'event-log'].includes(activeTab) ||
      (activeTab === 'summary' && summaryHiddenForCase)
    ) {
      onTabChange('chat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseSummary.id, refreshKey]);

  // Load audit entries when the Event Log tab is selected (and reload on
  // case switch / refresh while it is the active tab).
  useEffect(() => {
    if (activeTab !== 'event-log') return;
    let cancelled = false;
    setAuditLoading(true);
    listAuditEntries(caseSummary.id)
      .then((res) => { if (!cancelled) setAuditEntries(res.items || []); })
      .catch(() => { if (!cancelled) setAuditEntries([]); })
      .finally(() => { if (!cancelled) setAuditLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, caseSummary.id, refreshKey]);

  // Load the latest COMPLETED review run's summary for the Summary tab.
  // Mirrors the legacy CaseDetail.tsx selection logic: listReviewRuns ->
  // filter COMPLETED -> sort by run_number desc -> getReviewRun on the
  // newest. Non-fatal: a case with no reviews just shows the EmptyState.
  // Also captures the latest run of ANY status (for hasReview +
  // review-in-progress detection on load) so a reload while a review is
  // running resumes polling instead of silently dropping it.
  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    setHasReview(false);
    listReviewRuns(caseSummary.id)
      .then((runs) => {
        const runItems = runs.items || [];
        if (cancelled) return;
        setHasReview(runItems.length > 0);
        const latestAny = runItems
          .slice()
          .sort((a, b) => (b.run_number ?? 0) - (a.run_number ?? 0))[0];
        // If the latest run is RUNNING (e.g. a reload mid-review), resume
        // polling so the UI reflects completion.
        if (latestAny && latestAny.status === 'RUNNING' && !pollingRunId) {
          setReviewInProgress(true);
          setPollingRunId(latestAny.id);
        }
        const latestCompleted = runItems
          .filter((r) => r.status === 'COMPLETED')
          .sort((a, b) => (b.run_number ?? 0) - (a.run_number ?? 0))[0];
        if (!latestCompleted) {
          if (!cancelled) setReviewRunDetail(null);
          return;
        }
        return getReviewRun(caseSummary.id, latestCompleted.id).then((d) => {
          if (!cancelled) setReviewRunDetail(d);
        });
      })
      .catch(() => {
        if (!cancelled) setReviewRunDetail(null);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseSummary.id, refreshKey]);

  // Refresh the latest-review-load (called after a review completes / is
  // cancelled so the Summary tab + hasReview + latestRun update). Re-runs
  // the same load as the effect above.
  const reloadReviewRuns = () => {
    listReviewRuns(caseSummary.id)
      .then((runs) => {
        const runItems = runs.items || [];
        setHasReview(runItems.length > 0);
        const latestCompleted = runItems
          .filter((r) => r.status === 'COMPLETED')
          .sort((a, b) => (b.run_number ?? 0) - (a.run_number ?? 0))[0];
        if (!latestCompleted) {
          setReviewRunDetail(null);
          return;
        }
        return getReviewRun(caseSummary.id, latestCompleted.id).then((d) =>
          setReviewRunDetail(d),
        );
      })
      .catch(() => setReviewRunDetail(null));
  };

  // Flag override (mark as false positive): PATCH /flags/{id}/override,
  // then reload the latest completed review run so the flag's
  // override_status + the recalculated score (excludes overridden flags)
  // are reflected. Reused by SummaryTab's flag list.
  const handleOverrideFlag = async (flagId: string, reason: string) => {
    await overrideFlag(flagId, reason);
    addToast('Flag marked as false positive', 'success');
    reloadReviewRuns();
  };

  // Restore an overridden flag (unmark as false positive): inverse of
  // handleOverrideFlag. DELETE /flags/{id}/override + reload the run so
  // the flag's points rejoin the score. Operator request 2026-10-26.
  const handleUnoverrideFlag = async (flagId: string) => {
    await unoverrideFlag(flagId);
    addToast('Flag restored', 'success');
    reloadReviewRuns();
  };

  // Poll a background review run every ~4s until COMPLETED/FAILED/CANCELLED.
  // Mirrors the legacy CaseDetail.tsx polling loop. On COMPLETED, refreshes
  // the review-run cache + case data + pushes a chat system message when
  // the run was kicked off from the Chat tab.
  useEffect(() => {
    if (!caseSummary.id || !pollingRunId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const detail = await getReviewRun(caseSummary.id, pollingRunId);
        if (cancelled) return;
        const st = detail.run.status;
        if (st === 'COMPLETED') {
          setReviewInProgress(false);
          setPollingRunId(null);
          setReviewFailedMsg(null);
          addToast('Review completed', 'success');
          // Refresh the Summary tab + hasReview + latestRun.
          reloadReviewRuns();
          // Reload case data so flags / tier / status update.
          getCase(caseSummary.id)
            .then((d) => {
              if (cancelled) return;
              setDetail(d);
              setCaseData((prev) => ({
                ...prev,
                ...d.case,
                type: prev.type || d.case?.type,
              }));
              onDocumentsChanged?.();
            })
            .catch(() => { /* non-fatal */ });
          // Push a "Review completed" system message into the chat when
          // the run was fired from the Chat tab. The externalMessage /
          // externalSeq pair is consumed by ChatTab (seq guards against
          // duplicate replays on remount).
          if (reviewStartedFrom === 'chat') {
            const flagCount = detail.flags?.length ?? 0;
            setExternalMessage({
              role: 'system',
              content:
                `Review completed \u2014 ${flagCount} red flag${
                  flagCount === 1 ? '' : 's'
                } triggered. Ask a question about the results.`,
              result_link: 'summary',
            });
            setExternalSeq((n) => n + 1);
          }
          setReviewStartedFrom(null);
        } else if (st === 'FAILED') {
          setReviewInProgress(false);
          setPollingRunId(null);
          setReviewFailedMsg(detail.run.error_message || 'Review failed');
          addToast(
            'Review failed: ' + (detail.run.error_message || 'unknown error'),
            'error',
          );
          setReviewStartedFrom(null);
        } else if (st === 'CANCELLED') {
          setReviewInProgress(false);
          setPollingRunId(null);
          setReviewFailedMsg(null);
          addToast('Review cancelled', 'info');
          reloadReviewRuns();
          setReviewStartedFrom(null);
        }
      } catch {
        // network blip - keep polling
      }
    };
    poll(); // immediate first poll
    const handle = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingRunId, caseSummary.id, reviewStartedFrom]);

  // Kick off a full rubric review (no public-data pull). 202 + run_id;
  // polling useEffect above drives the rest. `from` records which tab
  // triggered the run so the completion handler knows whether to push a
  // chat system message.
  const handleRunReview = async (from: 'overview' | 'chat' = 'overview') => {
    setReviewLoading(true);
    setReviewFailedMsg(null);
    try {
      const resp = await runFullReview(caseSummary.id);
      setReviewStartedFrom(from);
      setReviewInProgress(true);
      setPollingRunId(resp.run_id);
      addToast('Review started', 'success');
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { detail?: string } } };
      if (e?.response?.status === 409) {
        addToast('A review is already in progress', 'error');
        setReviewInProgress(true);
      } else {
        addToast(e?.response?.data?.detail || 'Failed to start review', 'error');
      }
    } finally {
      setReviewLoading(false);
    }
  };

  // Re-pull public data + auto-launch a review (single "Update &
  // Re-review" flow). The backend returns the new review run_id; we feed
  // it to the polling loop.
  const handleReReview = async (from: 'overview' | 'chat' | 'summary' = 'overview') => {
    setRereviewLoading(true);
    setReviewFailedMsg(null);
    try {
      const resp = await reReviewCase(caseSummary.id);
      setReviewStartedFrom(from);
      addToast('Re-pulling public data + re-reviewing...', 'info');
      setPollingRunId(resp.run_id);
      setReviewInProgress(true);
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      addToast(e?.response?.data?.detail || 'Failed to re-review case', 'error');
    } finally {
      setRereviewLoading(false);
    }
  };

  // Cancel the in-progress review run. The poll loop picks up
  // status='CANCELLED' and clears in-progress.
  const handleCancelReview = async () => {
    if (!pollingRunId) return;
    setCancellingReview(true);
    try {
      await cancelReview(caseSummary.id, pollingRunId);
      addToast('Review cancelled', 'info');
    } catch (err) {
      const e = err as { response?: { status?: number } };
      if (e?.response?.status === 409) {
        addToast('Review is no longer running', 'info');
        setReviewInProgress(false);
        setPollingRunId(null);
        reloadReviewRuns();
      } else {
        addToast('Failed to cancel review', 'error');
      }
    } finally {
      setCancellingReview(false);
    }
  };

  const handleDownloadDocx = async () => {
    setDownloadingDocx(true);
    try {
      await downloadReviewRecordDocx(caseData.id, caseData.case_number || caseData.id);
    } finally {
      setDownloadingDocx(false);
    }
  };

  // Flag Memorandum export (DIR-045): uses the latest COMPLETED review
  // run for the case (reviewRunDetail.run.id). Disabled when no completed
  // run is loaded yet. Same RBAC as the review-record export.
  const handleDownloadFlagMemo = async (deterministic?: boolean) => {
    const runId = reviewRunDetail?.run?.id;
    if (!runId) return;
    setDownloadingFlagMemo(true);
    try {
      await downloadFlagMemorandumDocx(runId, caseData.case_number || caseData.id, deterministic);
    } finally {
      setDownloadingFlagMemo(false);
    }
  };

  // Flag Memorandum via Claude Anthropic API (Path C): produces a .md via
  // the Anthropic Messages API with PII tagged + reconstructed locally. No
  // Path-B-style admin consent required (no client OAuth session; PII is
  // tagged). Disabled when no completed run is loaded.
  const handleDownloadFlagMemoClaudeApi = async () => {
    const runId = reviewRunDetail?.run?.id;
    if (!runId) return;
    setDownloadingFlagMemoClaudeApi(true);
    try {
      await downloadFlagMemorandumClaudeApiMd(runId, caseData.case_number || caseData.id);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        'Failed to export Flag Memorandum via Claude API';
      setSummaryError(detail);
    } finally {
      setDownloadingFlagMemoClaudeApi(false);
    }
  };

  // Regenerate the AI summary for the latest COMPLETED review run. Calls
  // POST /cases/{id}/summary/regenerate and refreshes the Summary tab's
  // summary in place (no full case reload). Errors surface via the pane's
  // external message banner.
  const handleRegenerateSummary = async () => {
    setRegeneratingSummary(true);
    try {
      const { summary } = await regenerateSummary(caseData.id);
      // Merge the refreshed summary into the cached ReviewRunDetail so the
      // tab re-renders with the new text without a full reload.
      setReviewRunDetail((prev) =>
        prev ? { ...prev, summary } : prev,
      );
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        'Failed to regenerate summary';
      setSummaryError(detail);
    } finally {
      setRegeneratingSummary(false);
    }
  };

  // Create or update a comparison tab. The compare flow calls this twice:
  // first with just the job id (opens the tab, shows "in progress"), then
  // with the summary + filenames + doc ids once the job completes. Each
  // unique job id maps to exactly one tab.
  const handleCompareJob = (
    jobId: string,
    summary?: string,
    originalFilename?: string,
    revisionFilenames?: string[],
    originalDocId?: string,
    revisionDocId?: string,
  ) => {
    const prev = comparisonsRef.current;
    const existing = prev.find((c) => c.jobId === jobId);
    const id = existing?.id ?? `cmp-${jobId}`;
    const label =
      makeCompareLabel(originalFilename, revisionFilenames) ||
      existing?.label ||
      'Comparison';
    const merged: ComparisonTab = {
      id,
      jobId,
      label,
      summary: summary ?? existing?.summary,
      originalFilename: originalFilename ?? existing?.originalFilename,
      revisionFilenames: revisionFilenames ?? existing?.revisionFilenames,
      originalDocId: originalDocId ?? existing?.originalDocId,
      revisionDocId: revisionDocId ?? existing?.revisionDocId,
    };
    const next = existing
      ? prev.map((c) => (c.jobId === jobId ? merged : c))
      : [...prev, merged];
    syncComparisons(next);
    if (summary) setCompareSummary(summary);
    // Only switch to the tab when it is newly created (not on the
    // results-update call), so re-running a compare from another tab
    // doesn't yank the user off their current view mid-update.
    if (!existing) onTabChange(id);
  };

  const closeComparison = (id: string) => {
    const next = comparisonsRef.current.filter((c) => c.id !== id);
    syncComparisons(next);
    if (activeTab === id) onTabChange('chat');
  };

  // Open the optional Public Data tab. When `pull` is true, also trigger a
  // refresh (bumps the nonce PublicDataTab watches).
  const openPublicData = (pull: boolean) => {
    // Comparison cases do not pull global/public data (operator
    // 2026-10-27): refuse to open the Public Data tab for them as
    // defense-in-depth behind the server-side 422 gate. Gated through
    // the single `isComparison` derivation below.
    if (isComparison) return;
    setShowPublicData(true);
    onTabChange('public-data');
    if (pull) setPublicDataRefreshNonce((n) => n + 1);
  };
  const closePublicData = () => {
    setShowPublicData(false);
    if (activeTab === 'public-data') onTabChange('chat');
  };

  const startEditTitle = () => {
    setTitleDraft(caseData.title || caseData.case_number || '');
    setEditingTitle(true);
  };
  const saveTitle = async () => {
    const v = titleDraft.trim();
    if (!v || v === (caseData.title || caseData.case_number)) {
      setEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    try {
      const updated = await updateCase(caseData.id, { title: v });
      setCaseData((prev) => ({ ...prev, title: updated.title ?? v }));
      setEditingTitle(false);
      // Notify parent so the left-sidebar case list refreshes the title.
      onDocumentsChanged?.();
    } catch {
      // best-effort; leave editable on failure
    } finally {
      setSavingTitle(false);
    }
  };

  if (loading && !detail) {
    return <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 size={24} className="animate-spin" /></div>;
  }
  if (error) {
    return <div className="flex-1 flex items-center justify-center text-red-600 text-sm">{error}</div>;
  }

  // The Summary tab surfaces the deposit-review run summary + flag
  // memorandum. It is not relevant to comparison cases (those have no
  // review run), so the tab is omitted for them.
  const showSummaryTab = caseData.type !== 'comparison';
  // Comparison cases exist ONLY to diff old vs new files via the
  // whitewash `/tools/compare` job (operator 2026-10-27). They must NOT
  // show the DR-only surfaces. Single derivation gates every tab-filter
  // below. Dropped for comparison: overview (DR case-facts grid +
  // checklist + pull-public-data entry), public-data (the "pull global
  // data" surface), summary (DR review-record summary). Kept: chat
  // (houses the ComparisonSetup diff UI), documents (upload old + new
  // files), event-log.
  const isComparison = caseData.type === 'comparison';
  const permanentTabs: { key: PermanentTab; label: string }[] = [
    { key: 'chat', label: 'Chat' },
    ...(isComparison ? [] : [{ key: 'overview' as PermanentTab, label: 'Overview' }]),
    ...(showSummaryTab ? [{ key: 'summary' as PermanentTab, label: 'Summary' }] : []),
    { key: 'documents', label: 'Documents' },
    { key: 'event-log', label: 'Event Log' },
  ];

  const tab = activeTab as string;
  // Resolve the active tab. Optional tabs (Public Data, comparisons) are
  // valid only while they are open; otherwise fall back to Chat.
  const activeComparison = comparisons.find((c) => c.id === tab);
  const publicDataOpen = showPublicData && !isComparison && tab === 'public-data';
  const renderTab: string =
    activeComparison ? activeComparison.id :
    publicDataOpen ? 'public-data' :
    permanentTabs.some((t) => t.key === tab) ? tab : 'chat';

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full" data-testid="case-detail-pane">
      {/* Header */}
      <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between flex-shrink-0 bg-white">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {editingTitle ? (
              <div className="flex items-center gap-1 min-w-0">
                <input
                  autoFocus
                  className="text-base font-semibold text-slate-900 rounded border border-primary-400 px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-400 min-w-0"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveTitle();
                    if (e.key === 'Escape') setEditingTitle(false);
                  }}
                  data-testid="case-title-input"
                />
                <button
                  onClick={saveTitle}
                  disabled={savingTitle}
                  className="p-1 rounded text-primary-600 hover:bg-primary-50 disabled:opacity-50"
                  title="Save title"
                  data-testid="case-title-save"
                >
                  {savingTitle ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                </button>
                <button
                  onClick={() => setEditingTitle(false)}
                  className="p-1 rounded text-slate-400 hover:bg-slate-100"
                  title="Cancel"
                >
                  <X size={15} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 min-w-0">
                <h1 className="text-base font-semibold text-slate-900 truncate" data-testid="case-title">
                  {caseData.title || caseData.case_number}
                </h1>
                <button
                  onClick={startEditTitle}
                  className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 shrink-0"
                  title="Edit case title"
                  data-testid="case-title-edit"
                >
                  <Edit2 size={13} />
                </button>
              </div>
            )}
            {caseData.status && <StatusBadge status={caseData.status} />}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {(caseData.entity_name || caseData.issuer_name || caseData.depositor_name || '—')
              + ' · ' + new Date(caseData.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Tabs: permanent (Chat / Overview / Documents) + optional closable
          tabs (Public Data, Comparison tabs) */}
      <div className="border-b border-slate-200 px-5 flex gap-1 bg-white flex-shrink-0 overflow-x-auto" role="tablist">
        {permanentTabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={renderTab === t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => onTabChange(t.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              renderTab === t.key
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
        {showPublicData && !isComparison && (
          <div
            role="tab"
            aria-selected={renderTab === 'public-data'}
            data-testid="tab-public-data"
            onClick={() => onTabChange('public-data')}
            className={`group inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              renderTab === 'public-data'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Database size={13} className="text-slate-400 group-hover:text-slate-500" />
            <span>Public Data</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); closePublicData(); }}
              className="ml-0.5 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
              aria-label="Close Public Data"
              data-testid="tab-close-public-data"
            >
              <X size={13} />
            </button>
          </div>
        )}
        {comparisons.map((c) => (
          <div
            key={c.id}
            role="tab"
            aria-selected={renderTab === c.id}
            data-testid={`tab-${c.id}`}
            onClick={() => onTabChange(c.id)}
            className={`group inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
              renderTab === c.id
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <GitCompare size={13} className="text-slate-400 group-hover:text-slate-500" />
            <span title={c.label}>{c.label}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); closeComparison(c.id); }}
              className="ml-0.5 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
              aria-label={`Close ${c.label}`}
              data-testid={`tab-close-${c.id}`}
            >
              <X size={13} />
            </button>
          </div>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {/*
          ChatTab is ALWAYS mounted (toggle `hidden` when not the active
          tab) + keyed by caseData.id so its state resets on case switch.
          This keeps in-flight poll loops / pending-request state alive
          across tab switches (chat -> documents -> chat) instead of
          unmounting + losing them. The durable case_request ledger
          (backend) handles durability across case switch + page reload;
          this mounted-hidden+keyed fix handles tab-switch smoothness.
          See backend/app/routers/case_requests.py + briefs/case-request-ledger.md.
        */}
        <div className={renderTab === 'chat' ? 'flex-1 overflow-hidden flex flex-col min-h-0' : 'hidden'}>
          <ChatTab
            key={caseData.id}
            caseData={caseData}
            onWhitewashJob={setWhitewashJobId}
            onDeficiencyJob={setDeficiencyJobId}
            onCompareJob={handleCompareJob}
            onDocumentsChanged={onDocumentsChanged}
            externalMessage={externalMessage}
            externalSeq={externalSeq}
            onRunReview={() => handleRunReview('chat')}
            onReReview={() => handleReReview('chat')}
            onCancelReview={handleCancelReview}
            reviewLoading={reviewLoading}
            rereviewLoading={rereviewLoading}
            reviewInProgress={reviewInProgress}
            cancellingReview={cancellingReview}
            hasReview={hasReview}
          />
        </div>
        {renderTab === 'overview' && (
          <div className="flex-1 overflow-y-auto">
            <OverviewTab
              caseData={caseData}
              deficiencyJobId={deficiencyJobId}
              whitewashJobId={whitewashJobId}
              compareSummary={compareSummary}
              refreshKey={refreshKey}
              onPullPublicData={() => openPublicData(true)}
              onOpenPublicData={() => openPublicData(false)}
              onDeficiencyJob={setDeficiencyJobId}
              onDocumentsChanged={onDocumentsChanged}
              facts={detail?.facts}
              onRunReview={() => handleRunReview('overview')}
              onReReview={() => handleReReview('overview')}
              onCancelReview={handleCancelReview}
              reviewLoading={reviewLoading}
              rereviewLoading={rereviewLoading}
              reviewInProgress={reviewInProgress}
              cancellingReview={cancellingReview}
              hasReview={hasReview}
              reviewFailedMsg={reviewFailedMsg}
            />
          </div>
        )}
        {showSummaryTab && renderTab === 'summary' && (
          <div className="flex-1 overflow-y-auto">
            <SummaryTab
              caseData={caseData}
              summary={reviewRunDetail?.summary ?? null}
              score={reviewRunDetail?.score ?? null}
              flags={reviewRunDetail?.flags ?? []}
              onOverrideFlag={handleOverrideFlag}
              onUnoverrideFlag={handleUnoverrideFlag}
              loading={summaryLoading}
              onDownloadDocx={handleDownloadDocx}
              downloading={downloadingDocx}
              onDownloadFlagMemo={handleDownloadFlagMemo}
              downloadingFlagMemo={downloadingFlagMemo}
              canDownloadFlagMemo={Boolean(reviewRunDetail?.run?.id)}
              onDownloadFlagMemoClaudeApi={handleDownloadFlagMemoClaudeApi}
              downloadingFlagMemoClaudeApi={downloadingFlagMemoClaudeApi}
              latestCompletedRunId={reviewRunDetail?.run?.id ?? null}
              onRegenerate={handleRegenerateSummary}
              onGenerate={() => handleReReview('summary')}
              regenerating={regeneratingSummary}
              reviewInProgress={reviewInProgress}
              rereviewLoading={rereviewLoading}
              error={summaryError}
              onDismissError={() => setSummaryError(null)}
            />
          </div>
        )}
        {/*
          DocumentsTab is ALWAYS mounted (toggle `hidden` when not the
          active tab) + keyed by caseData.id so its state resets on case
          switch. This keeps the durable 'Processing N file(s)...' banner
          + its in-flight poll loops alive across tab switches (documents
          -> chat -> documents) instead of unmounting + losing them. The
          durable case_request ledger (backend) handles durability across
          case switch + page reload; this mounted-hidden+keyed fix handles
          tab-switch smoothness. Mirrors the ChatTab pattern above.
        */}
        <div className={renderTab === 'documents' ? 'flex-1 overflow-y-auto' : 'hidden'}>
          <DocumentsTab
            key={caseData.id}
            caseId={caseData.id}
            refreshKey={refreshKey}
            onDocumentsChanged={onDocumentsChanged}
            onCompareJob={handleCompareJob}
            onExternalMessage={(msg) => {
              setExternalMessage(msg);
              setExternalSeq((n) => n + 1);
            }}
          />
        </div>
        {activeComparison && (
          <div className="flex-1 overflow-y-auto">
            <DiffTab
              caseId={caseData.id}
              compareJobId={activeComparison.jobId}
              refreshKey={refreshKey}
              originalFilename={activeComparison.originalFilename}
              revisionFilenames={activeComparison.revisionFilenames}
              originalDocId={activeComparison.originalDocId}
              revisionDocId={activeComparison.revisionDocId}
            />
          </div>
        )}
        {publicDataOpen && (
          <div className="flex-1 overflow-y-auto">
            <PublicDataTab
              caseData={caseData}
              refreshNonce={publicDataRefreshNonce}
            />
          </div>
        )}
        {renderTab === 'event-log' && (
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Event Log</h2>
            {auditLoading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 size={16} className="animate-spin" /> Loading audit entries…
              </div>
            ) : auditEntries.length === 0 ? (
              <div className="text-sm text-slate-500">
                No audit entries yet. Actions on this case will appear here.
              </div>
            ) : (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
                <div className="space-y-4">
                  {auditEntries.map((entry) => (
                    <div key={entry.id} className="relative pl-12">
                      <div className="absolute left-2.5 top-1 w-3 h-3 rounded-full bg-primary-500 border-2 border-white" />
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-slate-700">{entry.action}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">{entry.entity_type}</span>
                        </div>
                        <p className="text-xs text-slate-400">{new Date(entry.timestamp).toLocaleString()}</p>
                        {entry.user_id && <p className="text-xs text-slate-500 mt-1">User: {entry.user_id}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}